import { FormEvent, useState } from 'react'
import { useCreateExhibitor } from '../api/hooks'
import { useCurrentShow } from '@/context/current-show'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { toast } from 'sonner'
import { PageHeading } from '@/components/page-heading'

export default function ExhibitorsPage() {
  const { currentShow } = useCurrentShow()
  const createExhibitor = useCreateExhibitor(currentShow?.id)
  const [form, setForm] = useState({ name: '', email: '', phone: '', affiliation: '' })

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    createExhibitor.mutate(
      {
        name: form.name.trim(),
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
        affiliation: form.affiliation.trim() || undefined,
      },
      {
        onSuccess: data => {
          toast.success(`Exhibitor created with id ${data.id}`)
        },
        onError: error => {
          toast.error(error.message)
        },
      }
    )
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <header className="space-y-2">
        <PageHeading>Exhibitors (advanced)</PageHeading>
        <p className="text-muted-foreground">
          Capture a walk-in entrant's contact details for this show.
        </p>
      </header>

      <div className="rounded-md border border-dashed bg-muted/40 p-4 text-sm text-muted-foreground">
        <p className="font-medium text-foreground">Prefer Check-In</p>
        <p className="mt-1">
          Check-In finds an existing person before adding a new one. This page remains available for
          adding a walk-in entrant directly.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Create exhibitor</CardTitle>
          <CardDescription>
            Provide at minimum a name and any optional contact fields to register a new exhibitor.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                type="text"
                value={form.name}
                onChange={event => setForm({ ...form, name: event.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={event => setForm({ ...form, email: event.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                type="tel"
                value={form.phone}
                onChange={event => setForm({ ...form, phone: event.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="affiliation">Affiliation</Label>
              <Input
                id="affiliation"
                type="text"
                value={form.affiliation}
                onChange={event => setForm({ ...form, affiliation: event.target.value })}
              />
            </div>

            {createExhibitor.isError && (
              <p className="text-destructive text-sm mt-2">{createExhibitor.error.message}</p>
            )}
          </form>
        </CardContent>
        <CardFooter className="flex justify-end">
          <Button
            type="submit"
            disabled={createExhibitor.isPending}
            onClick={e => handleSubmit(e as any)}
          >
            {createExhibitor.isPending ? 'Creating…' : 'Create Exhibitor'}
          </Button>
        </CardFooter>
      </Card>
    </div>
  )
}
